import { cn } from "@/lib/utils";
import type { FeedbackState } from "@/types/assessment";
import {
  CheckCircle2Icon,
  XCircleIcon,
  CircleDotIcon,
} from "lucide-react";
import { MarkdownContent } from "@/components/ui/markdown-content";

interface FeedbackPanelProps {
  feedback: FeedbackState;
}

export function FeedbackPanel({
  feedback,
}: FeedbackPanelProps) {
  const { isCorrect, score, maxScore, explanation } = feedback;

  // Blind-mode assessments omit grading info: render a neutral confirmation panel
  // instead of the green/red branching, which would otherwise show "Incorrect"
  // (because `!undefined` is truthy).
  if (isCorrect === undefined) {
    return (
      <div className="rounded-lg p-4 space-y-2 animate-in fade-in slide-in-from-top-2 duration-200 bg-muted/40 border">
        <div className="flex items-center gap-2">
          <CircleDotIcon className="size-5 text-muted-foreground" />
          <span className="font-semibold text-muted-foreground">
            Answer recorded
          </span>
        </div>
      </div>
    );
  }

  // Determine feedback variant
  const isPartial = !isCorrect && score !== undefined && score > 0;

  // Score display for LLM-evaluated questions
  const scoreDisplay =
    score !== undefined && maxScore !== undefined
      ? `${score}/${maxScore}`
      : score !== undefined
        ? `${score}`
        : null;

  return (
    <div
      className={cn(
        "rounded-lg p-4 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200",
        isCorrect && "bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800",
        !isCorrect && !isPartial && "bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800",
        isPartial && "bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800"
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isCorrect && (
            <>
              <CheckCircle2Icon className="size-5 text-green-600" />
              <span className="font-semibold text-green-700 dark:text-green-400">
                Correct!
              </span>
            </>
          )}
          {!isCorrect && !isPartial && (
            <>
              <XCircleIcon className="size-5 text-red-600" />
              <span className="font-semibold text-red-700 dark:text-red-400">
                Incorrect
              </span>
            </>
          )}
          {isPartial && (
            <>
              <CircleDotIcon className="size-5 text-amber-600" />
              <span className="font-semibold text-amber-700 dark:text-amber-400">
                Partially Correct
              </span>
            </>
          )}
        </div>

        {scoreDisplay && (
          <span
            className={cn(
              "text-sm font-medium px-2 py-1 rounded",
              isCorrect && "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300",
              !isCorrect && !isPartial && "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300",
              isPartial && "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300"
            )}
          >
            Score: {scoreDisplay}
          </span>
        )}
      </div>

      {/* Explanation */}
      {explanation && <MarkdownContent>{explanation}</MarkdownContent>}
    </div>
  );
}
